import useTlmCart from '@/composable/useTlmCart'
import useCartScenarios from '@/composable/useCartScenarios'
import useCategories from '@/composable/useCategories'
import useShoppingList from '@/composable/useShoppingList'
import { CHECKOUT_FLOW } from '~/constants/CheckoutFlow'
import { PRODUCT_TYPE } from '~/constants/ProductType'

export const GENERIC_CATEGORY_SLUGS = new Set(['subscriptions', 'mobilabonnement'])
export const MV_ONLY_FLOW_SECTIONS = new Set(['mobilevoice', 'play'])

const isMobileVoiceSubscription = (productTypeKey) => {
  return productTypeKey === PRODUCT_TYPE.MOBILE_SUBSCRIPTION
}

const isMobileBroadBandSubscription = (productTypeKey) => {
  return productTypeKey === PRODUCT_TYPE.MOBILE_BROADBAND_SUBSCRIPTION
}

const getCartItems = (orderCart) => {
  return orderCart?.cartItemGroupList?.flatMap(group => group.cartItems || []) || []
}

export const getSpecificCategorySlug = (categoryStr) => {
  if (!categoryStr) {
    return null
  }
  const slugs = categoryStr.split(', ')
  return slugs.find(slug => !GENERIC_CATEGORY_SLUGS.has(slug)) || null
}

export const resolveMvOnlyFlowSectionFromCart = (orderCart) => {
  const isPlay = getCartItems(orderCart).some((cartItem) => {
    return cartItem?.productType === 'SUBSCRIPTION' &&
      cartItem?.cartProduct?.additionalBillingInformation?.bundleType === 'MEDIA'
  })
  return isPlay ? 'play' : 'mobilevoice'
}

const hasMobileVoiceSubscription = (cartItems) => {
  return cartItems.some(item => isMobileVoiceSubscription(item?.cartProduct?.productTypeKey))
}

const hasMobileBroadBandSubscriptionInCart = (cartItems) => {
  return cartItems.some(item => isMobileBroadBandSubscription(item?.cartProduct?.productTypeKey))
}

const isMvOnlyCart = (groups) => {
  return groups.every((group) => {
    const items = group.cartItems || []
    return items.length > 0 &&
      items.every(item => isMobileVoiceSubscription(item?.cartProduct?.productTypeKey))
  })
}

const isEmptyCheckoutCart = (groups, cartItems) => {
  return !groups.length || !cartItems.length
}

const resolveCheckoutFlow = (groups, cartItems) => {
  if (isMvOnlyCart(groups)) {
    return CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY
  }

  if (hasMobileVoiceSubscription(cartItems)) {
    return CHECKOUT_FLOW.DEVICE_WITH_SUBSCRIPTION
  }

  if (hasMobileBroadBandSubscriptionInCart(cartItems)) {
    return CHECKOUT_FLOW.WITH_MOBILE_BROADBAND_SUBSCRIPTION
  }

  return CHECKOUT_FLOW.WITH_OUT_SUBSCRIPTION
}

export const resolveCheckoutTypeFromCart = (orderCart) => {
  const groups = orderCart?.cartItemGroupList || []
  const cartItems = getCartItems(orderCart)
  if (isEmptyCheckoutCart(groups, cartItems)) {
    return CHECKOUT_FLOW.NO_FLOW
  }

  return resolveCheckoutFlow(groups, cartItems)
}

export const resolvePrimarySiteSection = (section, additionalSiteSections = []) => {
  if (section && !GENERIC_CATEGORY_SLUGS.has(section)) {
    return section
  }
  const flowSection = additionalSiteSections.find(slug => MV_ONLY_FLOW_SECTIONS.has(slug))
  return flowSection || null
}

const resolveProductAddOnLabel = (shoppingListItems, activeShoppingListItem) => {
  if (!shoppingListItems || shoppingListItems.length <= 1) {
    return 'No ProductAddOn'
  }
  const activeItemIndex = shoppingListItems.indexOf(activeShoppingListItem)
  return `ProductAddOn ${activeItemIndex + 1} out of ${shoppingListItems.length}`
}

const resolveCategorySlug = (checkoutType, categorySlug, orderCart) => {
  if (categorySlug && !GENERIC_CATEGORY_SLUGS.has(categorySlug)) {
    return categorySlug
  }
  if (checkoutType === CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY) {
    return resolveMvOnlyFlowSectionFromCart(orderCart)
  }
  return null
}

export const captureReceiptTrackingSnapshot = (app, orderCart) => {
  try {
    const { getCategoryForCartItemGroup } = useTlmCart(app)
    const { getAttrFromCartScenario } = useCartScenarios(app)
    const { getCategorySlugById } = useCategories(app.$hubble)
    const { shoppingList, activeShoppingListItem } = useShoppingList(app)

    const checkoutType = resolveCheckoutTypeFromCart(orderCart)
    const categoryId = getCategoryForCartItemGroup() || getAttrFromCartScenario(orderCart, 'categoryId')
    const shoppingListItems = shoppingList.value?.shoppingListItem
    const rawCategorySlug = getCategorySlugById(categoryId) || null

    return {
      checkoutType,
      categorySlug: resolveCategorySlug(checkoutType, rawCategorySlug, orderCart),
      productAddOnLabel: resolveProductAddOnLabel(shoppingListItems, activeShoppingListItem.value)
    }
  } catch (error) {
    console.warn('captureReceiptTrackingSnapshot:', error)
    return null
  }
}
